import React, { useState, useEffect, useRef } from "react";
import { Search, Settings } from "lucide-react";
import { useSelector } from "react-redux";
import {
  fetchMemberApi,
  fetchAppUsageDataAPI,
} from "../../actioncreators/actioncreators";
import AddMember from "./AddMember";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import "./dashboard.css";
import Dialog from "@mui/material/Dialog";
import DigitalWellbeingChart from "./AppUsageChart";
import { toast } from "react-toastify";

const UserAvatar = ({ member }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (fullName) => {
    return fullName ? fullName[0].toUpperCase() : "";
  };

  // If there's no image or if there was an error loading it, show initials
  if (!member.user_image || imageError) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
        {getInitials(member.full_name)}
      </div>
    );
  }

  // Try to render the image
  return (
    <img
      src={`data:image/jpeg;base64,${member.user_image}`}
      alt={member.full_name}
      className="w-8 h-8 rounded-full object-cover"
      onError={() => setImageError(true)}
    />
  );
};

const AppUsage = () => {
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);

  // Super Admin / Admin org filtering support
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';
  const isAdmin = loginResponse?.role_name?.toUpperCase() === 'ADMIN' || loginResponse?.roleName?.toUpperCase() === 'ADMIN';
  // Org/sub-org context to pass for Super Admin (never org_id for Admin -
  // their own org is implicit via the JWT header) when drilled into a
  // specific org/sub-org via the header dropdown.
  const effectiveOrgId = isSuperAdmin && superAdminSelectedOrgId ? superAdminSelectedOrgId : null;

  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [response, setResponse] = useState("");
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;

  const handleAddMemberSuccess = () => {
    fetchMembers(); // Refresh member list after a new member is added
    setIsAddMemberOpen(false); // Close the dialog after success
  };

  useEffect(() => {
    fetchMembers();
  }, [currentPage, globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  const fetchMembers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        pageNumber: currentPage,
        pageSize: pageSize
      };
      // Add location filter if selected
      if (globalLocationFilter) {
        payload.location_id = globalLocationFilter;
      }

      // Add org_id for Super Admin viewing specific organization
      if (isSuperAdmin && superAdminSelectedOrgId) {
        payload.org_id = superAdminSelectedOrgId;
        if (superAdminSelectedSubOrgId) {
          payload.sub_org_id = superAdminSelectedSubOrgId;
        }
      } else if (isAdmin && superAdminSelectedSubOrgId) {
        // Admin drilled into a sub-org via the header dropdown. No org_id
        // override needed/valid for Admin (their own org is implicit via the
        // JWT header) - only sub_org_id.
        payload.sub_org_id = superAdminSelectedSubOrgId;
      }

      const response = await fetchMemberApi(payload); // Fetch from your API

      // Log the entire response to debug
      console.log("API Response:", response);

      // Accessing the data inside the response
      const { businessStatusCode, summaryData, totalPages, totalRecords } = response.data;

      // Check if the response contains the data as expected
      if (businessStatusCode === 2 && summaryData) {
        setMembers(summaryData); // Set the state with the summaryData
        setTotalPages(totalPages || 1);
        setTotalRecords(totalRecords || summaryData.length);
      } else {
        setMembers([]); // Set empty array for consistent state
        setTotalPages(1);
        setTotalRecords(0);
        setError("No members found. Please add members to get started.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMembers = members?.filter(
    (member) =>
      member?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.employee_id?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );
  const handleLocationClick = async (member) => {
    // Update the map center based on the clicked location
    setSelectedMemberId(member.user_id);
    const requestBody = {
      userId: member.user_id, // Send UserID from the clicked member
      date: new Date().toISOString().split("T")[0], // Gets only the date part in 'YYYY-MM-DD' format
    };
    // Super Admin drilled into an org via the header's Global Filter: scope
    // the selected member's app usage data to that org (see
    // FetchAppUsageDetails, which validates this server-side).
    if (effectiveOrgId) requestBody.org_id = effectiveOrgId;

    try {
      const response = await fetchAppUsageDataAPI(requestBody); // Fetch from your API

      const { businessStatusCode } = response;

      // Check if the response contains the data as expected
      if (businessStatusCode === 2 ) {
      setResponse(response);
      }  else {
        toast.info("Tracking details not found");
        fetchMembers();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Show "Select Organization" message if Super Admin hasn't selected an org
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen" style={{ backgroundColor: "#EFF0F4" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">App Usage</h1>
            <p className="text-sm text-gray-1000">Super Admin View</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-12 mt-8">
          <svg
            className="w-24 h-24 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center max-w-md">
            Please select an organization from the dropdown in the header to view app usage data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid m-0 p-0" >
      <div className="row " style={{backgroundColor:'#eff0f4'}}>
        <div className="col-12 col-md-12 d-flex align-items-center mt-3  border-bottom text-black-50 ">
          <div className="relative w-64 ">
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 placeholder-gray-1000 focus:outline-none focus:border-gray-300 focus:ring-0 mb-3 shadow " style={{backgroundColor:'#ffffff'}}
              placeholder="Search User"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Members Table */}
        <div className="col-12 col-md-3 text-center border-end text-black-50 shadow rounded" style={{backgroundColor:'#ffffff'}}>
          {isLoading ? (
            <div className="col-12 text-center mt-2">Loading...</div>
          ) : error ? (
            <div className="col-12 text-center mt-2 text-red-1000">{error}</div>
          ) : (
            <div className="d-flex flex-column" style={{ height: '100%', maxHeight: 'calc(100vh - 150px)' }}>
              {/* Table Header - Fixed */}
              <p className="mt-2 border-bottom text-black-50 fw-bold w-full items-center pb-3 text-sm" style={{ flexShrink: 0 }}>
                Users List
              </p>

              {/* Table Body - Scrollable */}
              <div style={{ 
                flex: '1',
                overflowY: 'auto',
                paddingRight: '5px',
                scrollbarWidth:'none',
                msOverflowStyle:'none',
                minHeight: '53vh'
              }}>
                {filteredMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className={`items-center px-1 py-1 shadow-sm ${selectedMemberId === member.user_id ? 'active' : ''}`}
                    onClick={() => handleLocationClick(member)}
                    style={{
                      padding: "25px",
                      backgroundColor: "#ffffff",
                      color: selectedMemberId === member.user_id ? "#FF8800" : "#333",
                      borderColor: selectedMemberId === member.user_id ? "#FF8800" : "#333",
                      marginBottom: "1rem",
                      border: selectedMemberId === member.user_id ? "2px solid #FF8800" : "1px solid #eeeeee",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      borderRadius: "20px",
                    }}
                  >
                    <div className=" items-center">
                      <div className="ml-3 flex items-center">
                        <UserAvatar member={member} />
                        <div className="ml-3">
                          <div className="text-sm font-medium " style={{ color:member.user_id === selectedMemberId ? "rgb(255, 136, 0)" : "#1f2937"}}>
                            {/* Show full_name if available, else fallback to email or user_id */}
                            {member.full_name && member.full_name.trim() !== "" && member.full_name !== "null"
                              ? member.full_name
                              : member.email || member.user_id}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls - Fixed at Bottom */}
              {totalPages > 1 && (
                <div className="d-flex flex-column align-items-center py-2 px-2 border-top" style={{ backgroundColor: '#ffffff', flexShrink: 0, gap: '4px' }}>
                  <div style={{ fontSize: '11px', color: '#888', fontWeight: 500 }}>
                    {currentPage} / {totalPages}
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0" style={{ gap: '2px' }}>
                      <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
                      </li>
                      <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                      </li>
                      {(() => {
                        const pages = [];
                        const delta = 2;
                        let lastPage = null;
                        for (let i = 1; i <= totalPages; i++) {
                          if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                            if (lastPage && i - lastPage > 1) {
                              pages.push(
                                <li key={`ellipsis-${i}`} className="page-item disabled">
                                  <span className="page-link" style={{ fontSize: '11px', padding: '3px 6px' }}>...</span>
                                </li>
                              );
                            }
                            pages.push(
                              <li key={i} className={`page-item${currentPage === i ? ' active' : ''}`}>
                                <button
                                  className="page-link"
                                  style={{
                                    fontSize: '11px', padding: '3px 7px',
                                    backgroundColor: currentPage === i ? '#FF8800' : '',
                                    borderColor: currentPage === i ? '#FF8800' : '',
                                    color: currentPage === i ? '#fff' : '',
                                  }}
                                  onClick={() => setCurrentPage(i)}
                                >{i}</button>
                              </li>
                            );
                            lastPage = i;
                          }
                        }
                        return pages;
                      })()}
                      <li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                      </li>
                      <li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="col-12 col-md-9 p-0 m-0">
          <DigitalWellbeingChart response={response} selectedMemberId={selectedMemberId}/>
        </div>

        <Dialog
          open={isAddMemberOpen}
          onClose={() => setIsAddMemberOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <AddMember
            isOpen={isAddMemberOpen}
            onClose={() => setIsAddMemberOpen(false)}
            onSuccess={handleAddMemberSuccess}
          />
        </Dialog>
      </div>
    </div>
  );
};

export default AppUsage;
